import * as React from 'react';
import { st, classes } from './WeeklyDatePicker.st.css';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { getWeekDatesRange, WeeklyDateNavigation, } from '../WeeklyDateNavigation';
import { getWeek, isSameDay, addDays, isPast, endOfDay } from 'date-fns';
import { DayPicker, Row, useDayRender, Button, } from 'react-day-picker-v8';
import { formatDateForDataAttr, formatDay, formatLongWeekDayName, formatNarrowWeekDayName, formatShortWeekDayName, getFirstAvailableDate, } from './util/dateUtils';
import { areDatesInTheSameWeek, getCaptionText, getOffsetWeekDatesRange, } from '../WeeklyDateNavigation/dateUtils/dateUtils';
import { useWeeklyDatePickerContext, WeeklyDatePickerProvider, } from './WeeklyDatePickerProvider';
import { KEYS } from '../../common/keyCodes';
import { IconButton } from '../IconButton';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { Text } from '../Text';
import { shellClasses } from '../../common/shellClasses';
/** A weekly Date Picker to select a specific date. */
const CaptionElement = ({ rtl, mobile, leftArrowAriaLabel, leftArrowAriaLabelledBy, rightArrowAriaLabel, rightArrowAriaLabelledBy, locale, firstDayOfWeek, removeOuterPadding, }) => {
    const Caption = (props) => {
        const { currentWeek, setCurrentMonth, setCurrentWeek } = useWeeklyDatePickerContext();
        const onChange = (week) => {
            setCurrentWeek(week);
            setCurrentMonth(week.startOfWeek);
        };
        return (React.createElement(WeeklyDateNavigation, { captionId: props.id, className: st(classes.header, {
                removeOuterPadding,
                isMobile: mobile,
                rtl,
            }), value: currentWeek, onChange: onChange, prevArrowAriaLabel: leftArrowAriaLabel, prevArrowAriaLabelledBy: leftArrowAriaLabelledBy, nextArrowAriaLabel: rightArrowAriaLabel, nextArrowAriaLabelledBy: rightArrowAriaLabelledBy, locale: locale, firstDayOfWeek: firstDayOfWeek }));
    };
    return Caption;
};
const WeekView = ({ firstDayOfWeek, }) => {
    const Week = (propsRow) => {
        const { currentWeek } = useWeeklyDatePickerContext();
        const weekNumber = getWeek(currentWeek.startOfWeek, {
            weekStartsOn: firstDayOfWeek,
        });
        if (!(weekNumber === propsRow.weekNumber)) {
            return React.createElement(React.Fragment, null);
        }
        return React.createElement(Row, { ...propsRow });
    };
    return Week;
};
const CustomDay = ({ dateIndication, locale, firstAvailableDate, groupWeekdaysAndDates, isMobile, }) => {
    const Day = (propsDay) => {
        const buttonRef = React.useRef(null);
        const dayRender = useDayRender(propsDay.date, propsDay.displayMonth, buttonRef);
        const day = propsDay.date;
        const isSelected = isSameDay(day, dayRender.selectedDays);
        const dateIndicationNode = dateIndication &&
            dateIndication({
                date: day,
                isSelected,
            });
        const buttonProps = { ...dayRender.buttonProps, tabIndex: 0 };
        const isFirstAvailableDate = isSameDay(firstAvailableDate, day);
        const calculateTabIndex = () => {
            if (isFirstAvailableDate) {
                return 0;
            }
            return -1;
        };
        return (React.createElement(Button, { ...buttonProps, ref: buttonRef, "data-date": formatDateForDataAttr(day), tabIndex: calculateTabIndex() },
            React.createElement("div", { className: st(classes.dayWrapper, {
                    hasIndication: !!dateIndicationNode,
                }) },
                groupWeekdaysAndDates && (React.createElement("span", { "aria-hidden": "true" }, isMobile
                    ? formatNarrowWeekDayName(day, locale)
                    : formatShortWeekDayName(day, locale))),
                React.createElement("span", { "aria-hidden": "true" }, day.getDate()),
                React.createElement("span", { className: st(classes.vhidden) }, formatDay(day, locale)),
                dateIndicationNode && !groupWeekdaysAndDates ? (React.createElement("div", { className: classes.dayIndicationContainer }, dateIndicationNode)) : null)));
    };
    return Day;
};
const WeeklyDatePickerContent = ({ locale = 'en', excludePastDates = false, filterDate = () => true, showMonthDropdown = false, showYearDropdown = false, dateIndication = () => null, className, value, onChange, today, firstDayOfWeek = 1, 'data-hook': dataHook, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, leftArrowAriaLabel, leftArrowAriaLabelledBy, rightArrowAriaLabel, rightArrowAriaLabelledBy, removeOuterPadding, arrowLocation = 'caption', groupWeekdaysAndDates = false, }) => {
    const { rtl, mobile } = useTPAComponentsContext();
    const { currentMonth, currentWeek, setCurrentMonth, setCurrentWeek } = useWeeklyDatePickerContext();
    const [prevValue, setPrevValue] = React.useState(value);
    const containerRef = React.useRef(null);
    const WeeklyView = React.useCallback(WeekView({ firstDayOfWeek }), [
        firstDayOfWeek,
    ]);
    const firstAvailableDate = React.useMemo(() => getFirstAvailableDate({
        startOfWeekDate: currentWeek.startOfWeek,
        filterDate,
        excludePastDates,
    }), [currentWeek.startOfWeek.getDate(), excludePastDates, filterDate]);
    const Day = React.useCallback(CustomDay({
        dateIndication,
        locale,
        firstAvailableDate,
        groupWeekdaysAndDates,
        isMobile: mobile,
    }), [dateIndication, firstAvailableDate, firstDayOfWeek, locale]);
    const Caption = React.useCallback(CaptionElement({
        rtl,
        mobile,
        leftArrowAriaLabelledBy,
        leftArrowAriaLabel,
        rightArrowAriaLabel,
        rightArrowAriaLabelledBy,
        locale,
        firstDayOfWeek,
        removeOuterPadding,
    }), [
        rtl,
        mobile,
        leftArrowAriaLabelledBy,
        leftArrowAriaLabel,
        rightArrowAriaLabel,
        rightArrowAriaLabelledBy,
        locale,
        firstDayOfWeek,
        removeOuterPadding,
    ]);
    const updateWeekRange = (weekRange) => {
        if (weekRange) {
            setCurrentWeek(weekRange, () => setCurrentMonth(weekRange.startOfWeek));
        }
        else {
            if (!isSameDay(value, prevValue)) {
                setPrevValue(value);
                const latestWeek = getWeekDatesRange(value, firstDayOfWeek);
                if (!areDatesInTheSameWeek(currentWeek.startOfWeek, latestWeek.startOfWeek, firstDayOfWeek)) {
                    setCurrentWeek(latestWeek, () => setCurrentMonth(latestWeek.startOfWeek));
                }
            }
        }
    };
    const focusDate = (date) => {
        const focusEl = containerRef.current.querySelector(`[data-date="${formatDateForDataAttr(date)}"]`);
        if (focusEl) {
            focusEl.focus();
        }
    };
    const handleArrowNavigation = React.useCallback((currentDate, offset) => {
        const newDate = addDays(currentDate, offset);
        const isEnabled = filterDate(newDate) && !(excludePastDates && isPast(endOfDay(newDate)));
        if (!isEnabled) {
            return;
        }
        if (!areDatesInTheSameWeek(newDate, currentWeek.startOfWeek, firstDayOfWeek)) {
            const newWeek = getWeekDatesRange(newDate, firstDayOfWeek);
            setCurrentWeek(newWeek, () => setCurrentMonth(newWeek.startOfWeek));
        }
        focusDate(newDate);
    }, [currentWeek, firstDayOfWeek, excludePastDates, filterDate]);
    const onDayKeyDownFactory = React.useCallback((currentDate, _modifiers, event) => {
        switch (event.key) {
            case KEYS.ArrowLeft:
                event.preventDefault();
                event.stopPropagation();
                rtl
                    ? handleArrowNavigation(currentDate, 1)
                    : handleArrowNavigation(currentDate, -1);
                break;
            case KEYS.ArrowRight:
                event.preventDefault();
                event.stopPropagation();
                rtl
                    ? handleArrowNavigation(currentDate, -1)
                    : handleArrowNavigation(currentDate, 1);
                break;
            case KEYS.Enter:
            case KEYS.Spacebar:
                event.preventDefault();
                event.target.click();
                focusDate(currentDate);
                break;
            default:
                break;
        }
    }, [rtl, handleArrowNavigation]);
    // this is needed because react-day-picker is expected to locale as date-fns but we are using Intl.
    // the formatter is cutomize in order to support Wix locale base on Intl and not date-fns.
    const formatWeekdayName = (date) => {
        return formatShortWeekDayName(date, locale);
    };
    const labelWeekday = (date) => {
        return formatLongWeekDayName(date, locale);
    };
    const { root, ...classesWithoutRoot } = classes;
    const classNames = {
        ...classesWithoutRoot,
        table: st(classes.table, { isMobile: mobile }),
        day: st(classes.day, 'DayPicker-Day', shellClasses.hasCustomFocus),
        head: classes.weekdays,
        head_cell: classes.weekday,
    };
    updateWeekRange();
    const weekAnnouncement = `Week starting ${new Intl.DateTimeFormat(locale, {
        weekday: 'long',
    }).format(currentWeek.startOfWeek)}, ${new Intl.DateTimeFormat(locale, {
        month: 'long',
        day: 'numeric',
    }).format(currentWeek.startOfWeek)}`;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: containerRef, className: st(root, { arrowLocation, rtl, groupWeekdaysAndDates }, className), "data-hook": dataHook, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, role: "region" },
            arrowLocation === 'dates' && (React.createElement(Text, { className: classes.caption, "data-hook": `${dataHook}-caption` }, getCaptionText(currentWeek, locale))),
            React.createElement("div", { className: classes.dayPickerContainer },
                arrowLocation === 'dates' && (React.createElement(IconButton, { className: classes.arrow, icon: React.createElement(ChevronLeft, null), "data-hook": "prev-arrow", onClick: () => {
                        const nextRange = getOffsetWeekDatesRange(currentWeek.startOfWeek, -1, {
                            locale,
                            firstDayOfWeek,
                        });
                        updateWeekRange(nextRange);
                    }, "aria-labelledby": leftArrowAriaLabelledBy, "aria-label": leftArrowAriaLabel })),
                React.createElement(DayPicker, { components: {
                        Caption: arrowLocation === 'caption' ? Caption : () => null,
                        Day,
                        Row: WeeklyView,
                        HeadRow: groupWeekdaysAndDates ? () => null : undefined,
                    }, showOutsideDays: true, mode: "single", selected: value, onSelect: onChange, className: classes.rdp, classNames: classNames, captionLayout: showMonthDropdown && showYearDropdown ? 'dropdown' : 'buttons', weekStartsOn: firstDayOfWeek, month: currentMonth, onDayKeyDown: onDayKeyDownFactory, disabled: [
                        (date) => !filterDate(new Date(date)),
                        ...(excludePastDates ? [{ before: new Date() }] : []),
                    ], today: today, formatters: { formatWeekdayName }, labels: { labelWeekday }, required: true }),
                arrowLocation === 'dates' && (React.createElement(IconButton, { className: classes.arrow, icon: React.createElement(ChevronRight, null), "data-hook": "next-arrow", onClick: () => {
                        const nextRange = getOffsetWeekDatesRange(currentWeek.startOfWeek, 1, {
                            locale,
                            firstDayOfWeek,
                        });
                        updateWeekRange(nextRange);
                    }, "aria-labelledby": rightArrowAriaLabelledBy, "aria-label": rightArrowAriaLabel })))),
        React.createElement("span", { "aria-live": "polite", "aria-atomic": "true", className: st(classes.vhidden) }, weekAnnouncement)));
};
export const WeeklyDatePicker = ({ value = new Date(), ...restProps }) => (React.createElement(WeeklyDatePickerProvider, { firstDayOfWeek: restProps.firstDayOfWeek, onDisplayedWeekChange: restProps.onDisplayedWeekChange, selectedDate: value },
    React.createElement(WeeklyDatePickerContent, { ...restProps, value: value })));
WeeklyDatePicker.displayName = 'WeeklyDatePicker';
//# sourceMappingURL=WeeklyDatePicker.js.map